import React, {
  ReactNode,
  useContext,
  useMemo,
  useCallback,
  ChangeEvent,
  memo,
} from 'react';
import { Grid } from '@mui/material';
import { EntityTypes, PermitType, Status } from 'types';
import { calculateEndOfTimePeriod, capitalize, formatLabel, getCountryFromStateCode } from 'utils';
import { AppContext } from 'context/appContext';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { useFetcher, useNavigate, useOutletContext, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import SettingsSection from 'components/layout/SettingsSection';
import DetailsHeader from 'components/layout/DetailsHeader';
import { PermitFormActionHandler, PermitFormContext } from './PermitFormLayout';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import AuturaDateTimePicker from 'components/inputs/AuturaDateTimePicker';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import AuturaStateField from 'components/inputs/AuturaStateField';
import AuturaMakeModelField from 'components/inputs/AuturaMakeModelField';
import { ROUTES, routeWithParams } from 'router/routes';
import FormFrame from 'components/layout/FormFrame';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';

const PermitDetails = (): ReactNode => {
  const { t } = useTranslation();
  const { permitID } = useParams();
  const { state } = useContext(AppContext);
  const navigate = useNavigate(); 
  const { fetcherKey, entity, displayMode, permit, setPermit, permitTypes } =
    useOutletContext<PermitFormContext>();
  const fetcher = useFetcher({ key: fetcherKey });
  const canEdit = state.entityPermissions.permit.update;

  // form handlers
  const handleTextFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (name.startsWith('permit.contact.')) {
        if (name.startsWith('permit.contact.location.')) {
          const field = name.replace('permit.contact.location.', '');
          setPermit({
            ...permit,
            contact: {
              ...permit.contact,
              location: { ...permit.contact.location, [field]: value },
            },
          });
        } else if (name.startsWith('permit.contact.data.driverLicense.')) {
          setPermit({
            ...permit,
            contact: {
              ...permit.contact,
              data: {
                ...permit.contact?.data,
                driverLicense: {
                  ...permit.contact?.data?.driverLicense,
                  number: value || '',
                  state: permit.contact?.data?.driverLicense?.state || '',
                },
              },
            },
          });
        } else {
          const field = name.replace('permit.contact.', '');
          setPermit({
            ...permit,
            contact: { ...permit.contact, [field]: value },
          });
        }
      } else if (name.startsWith('permit.vehicle.')) {
        const field = name.replace('permit.vehicle.', '');
        if (field === 'plate.number') {
          const formatted = value.replace(/[^a-zA-Z0-9]/g, '');
          setPermit({
            ...permit,
            vehicle: {
              ...permit.vehicle,
              plate: {
                ...permit.vehicle.plate,
                number: formatted.toUpperCase(),
              },
            },
          });
        } else if (field === 'vin') {
          const formatted = value.replace(/[^a-zA-Z0-9]/g, '');
          setPermit({
            ...permit,
            vehicle: { ...permit.vehicle, vin: formatted.toUpperCase() },
          });
        } else {
          setPermit({
            ...permit,
            vehicle: { ...permit.vehicle, [field]: value },
          });
        }
      } else {
        const field = name.replace('permit.', '');
        if (field === 'data.spot') {
          setPermit({
            ...permit,
            data: { ...permit.data, spot: value },
          });
        } else {
          setPermit({
            ...permit,
            [field]: value,
          });
        }
      }
    },
    [permit],
  );
  const handleCancel = () => {
    navigate(routeWithParams(ROUTES.PROPERTY_PERMITS, [state.tenant.id, entity.id]));
  };
  const handleSetStartTime = (date: Date | null) => {
    setPermit({
      ...permit,
      startTime: date ? date.getTime() : 0,
    });
  };
  const handleSetEndTime = (date: Date | null) => {
    setPermit({
      ...permit,
      endTime: date ? date.getTime() : 0,
    });
  };
  const handleSetStateCode = (stateCode: string) => {
    const country = getCountryFromStateCode(stateCode);
    setPermit({
      ...permit,
      contact: {
        ...permit.contact,
        id: permit.contact?.id || 0,
        status: permit.contact?.status || Status.Active,
        tenantID: state.tenant.id,
        name: permit.contact?.name || '',
        location: {
          ...permit.contact?.location,
          stateCode,
          country,
        },
      },
    });
  };
  const handleSetPhoneNumber = (phoneNumber: string) => {
    setPermit({
      ...permit,
      contact: {
        ...permit.contact,
        primaryPhone: phoneNumber,
      },
    });
  };
  const handleSetDriverLicenseState = (stateCode: string) => {
    setPermit({
      ...permit,
      contact: {
        ...permit.contact,
        data: {
          ...permit.contact?.data,
          driverLicense: {
            ...permit.contact?.data?.driverLicense,
            state: stateCode || '',
            number: permit.contact?.data?.driverLicense?.number || '',
          },
        },
      },
    });
  };
  const handleSetVehiclePlateState = (stateCode: string) => {
    setPermit({
      ...permit,
      vehicle: {
        ...permit.vehicle,
        plate: {
          ...permit.vehicle.plate,
          state: stateCode,
        },
      },
    });
  };
  const handleSetPermitType = (e: ChangeEvent<HTMLInputElement>) => {
    const permitType = permitTypes.find(
      (pt: PermitType) => pt.name === e.target.value,
    );
    if (permitType) {
      const now = new Date();
      const updatedPermit = {
        ...permit,
        permitType: permitType,
      };
      
      // In create mode, update the end time based on the selected permit type's default term
      if (permit.id === 0) {
        updatedPermit.endTime = calculateEndOfTimePeriod(
          now,
          permitType.data.defaultTerm,
        ).getTime();
      }
      
      setPermit(updatedPermit);
    }
  };
  const handleSubmit = () => {
    const updatedPermit = { ...permit };
    if (permit.vehicle && permit.vehicle.year) {
      const numericYear = parseInt(`${permit.vehicle.year}`, 10);
      updatedPermit.vehicle.year = numericYear;
    }

    if (permit.id > 0) {
      // update existing permit
      fetcher.submit(
        {
          body: JSON.stringify(updatedPermit),
          handler: PermitFormActionHandler.UPDATE,
          permitID: permit.id,
          permitTypeID: permit.permitType?.id || 0,
          entityType:
            displayMode === 'property'
              ? EntityTypes.Property
              : EntityTypes.PropertyGroup,
          entityID: entity.id,
          tenantID: state.tenant.id,
        },
        {
          method: 'PUT',
          action: ROUTES.PROPERTY_PERMIT_DETAILS,
          encType: 'application/json',
        },
      );
    }

    if (permit.id === 0) {
      // create new permit
      if (permit.contact?.location) {
        updatedPermit.contact.location.id = 0;
      }
      fetcher.submit(
        {
          body: JSON.stringify(updatedPermit),
          handler: PermitFormActionHandler.CREATE,
          permitTypeID: permit.permitType?.id || 0,
          permitID: permit.id,
          entityType:
            displayMode === 'property'
              ? EntityTypes.Property
              : EntityTypes.PropertyGroup,
          entityID: entity.id,
          tenantID: state.tenant.id,
        },
        {
          method: 'POST',
          action: ROUTES.PROPERTY_PERMIT_DETAILS,
          encType: 'application/json',
        },
      );
    }
  };

  const submitLabel = useMemo(() => {
    if (permitID !== '0') {
      return t('buttons.update', {
        name: t('permit.label', { ns: 'entities' }),
      });
    } else {
      return t('buttons.create', {
        name: t('permit.label', { ns: 'entities' }),
      });
    }
  }, [permitID]);

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={permit.id ? t('details') : ''}
          submitLabel={submitLabel}
          disableSubmit={fetcher.state !== 'idle'}
          onCancel={handleCancel}
        />
        <ScrollableFormContainer
          maxHeight={`calc(100vh - ${
            permit.id ? '454px' : '326px' 
          })`}
        >
          <SettingsSection title={permit.id ? t('subheadings.details', { ns: 'permits' }) : ''}>
            <Grid container width={'100%'} spacing={1.5}>
              {permit.id > 0 && (
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                    lg: 3,
                  }}
                >
                  <AuturaTextField
                    label={t('permit.permitNumber', { ns: 'entities' })}
                    name={'permit.permitNumber'}
                    value={permit.permitNumber || ''}
                    readonly
                  />
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  name='permit.permitType'
                  label={t('permit_type.label', { ns: 'entities' })}
                  value={permit.permitType.name}
                  setValue={handleSetPermitType}
                  options={permitTypes.map((pt) => pt.name)}
                  readonly={!canEdit || permit.id > 0}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaDateTimePicker
                  label={t('permit.startTime', { ns: 'entities' })}
                  readonly={permit.id > 0 || !canEdit}
                  dateTime={
                    permit.startTime ? new Date(permit.startTime) : null
                  }
                  setDateTime={handleSetStartTime}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaDateTimePicker
                  readonly={!canEdit}
                  label={t('permit.endTime', { ns: 'entities' })}
                  dateTime={permit.endTime ? new Date(permit.endTime) : null}
                  setDateTime={handleSetEndTime}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.data.spot', { ns: 'entities' })}
                  name={'permit.data.spot'}
                  value={permit.data?.spot || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.permitStatus', { ns: 'entities' })}
                  name={'permit.permitStatus'}
                  value={formatLabel(permit.permitStatus) || ''}
                  readonly
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.inviteStatus', { ns: 'entities' })}
                  name={'permit.inviteStatus'}
                  value={formatLabel(permit.inviteStatus) || ''}
                  readonly
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.holder', {
              ns: 'permits',
            })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name={'permit.contact.name'}
                  label={t('contact.name', { ns: 'entities' })}
                  value={permit?.contact?.name || ''}
                  readonly={!canEdit}
                  onChange={handleTextFieldChange}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.primaryEmail', { ns: 'entities' })}
                  name={'permit.contact.primaryEmail'}
                  value={permit?.contact?.primaryEmail || ''}
                  validators={['isEmail']}
                  errorMessages={['Invalid email']}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t(`contact.primaryPhoneMobile`, { ns: 'entities' })}
                  name={'permit.contact.primaryPhone'}
                  typeFieldName={'permit.contact.primaryPhoneType'}
                  typeFieldLabel='Type'
                  phoneNumber={permit?.contact?.primaryPhone || ''}
                  setPhoneNumber={handleSetPhoneNumber}
                  phoneType={permit?.contact?.primaryPhoneType}
                  setPhoneType={(phoneType) => {
                    if (permit?.contact) {
                      const updatedPermit = {
                        ...permit,
                        contact: {
                          ...permit.contact,
                          primaryPhoneType: phoneType,
                        },
                      };
                      setPermit(updatedPermit);
                    }
                  }}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.data.driverLicense.number', {
                    ns: 'entities',
                  })}
                  name='permit.contact.data.driverLicense.number'
                  value={permit?.contact?.data?.driverLicense?.number || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                  disabled={
                    !permit?.permitType?.data?.fieldConfig?.driverLicense
                      ?.enabled
                  }
                  required={
                    permit?.permitType?.data?.fieldConfig?.driverLicense
                      ?.required
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaStateField
                  label={t('permit.contact.data.driverLicense.state', {
                    ns: 'entities',
                  })}
                  readonly={!canEdit}
                  stateCode={permit?.contact?.data?.driverLicense?.state || ''}
                  setStateCode={handleSetDriverLicenseState}
                  required={
                    permit?.permitType?.data?.fieldConfig?.driverLicense
                      ?.required
                  }
                  disabled={
                    !permit?.permitType?.data?.fieldConfig?.driverLicense
                      ?.enabled
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.street', {
                    ns: 'entities',
                  })}
                  name='permit.contact.location.street'
                  value={permit.contact?.location?.street || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.unit', { ns: 'entities' })}
                  name='permit.contact.location.unit'
                  value={permit.contact?.location?.unit || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.city', { ns: 'entities' })}
                  name='permit.contact.location.city'
                  value={permit.contact?.location?.city || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaStateField
                  label={t('permit.contact.location.stateCode', {
                    ns: 'entities',
                  })}
                  readonly={!canEdit}
                  stateCode={permit.contact?.location?.stateCode as string}
                  setStateCode={handleSetStateCode}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.postalCode', {
                    ns: 'entities',
                  })}
                  name='permit.contact.location.postalCode'
                  value={permit.contact?.location?.postalCode || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.vehicle', {
              ns: 'permits',
            })}
            divider={false}
          >
            <Grid
              container
              width={'100%'}
              spacing={1.5}
              sx={{ pb: { xs: '128px', md: '0px' } }}
            >
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaTextField
                  label={t('permit.vehicle.plate.number', { ns: 'entities' })}
                  name='permit.vehicle.plate.number'
                  value={permit?.vehicle?.plate?.number || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaStateField
                  label={t('permit.vehicle.plate.state', { ns: 'entities' })}
                  readonly={!canEdit}
                  stateCode={permit?.vehicle?.plate?.state || ''}
                  setStateCode={handleSetVehiclePlateState}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaTextField
                  label={t('permit.vehicle.year', { ns: 'entities' })}
                  name={'permit.vehicle.year'}
                  value={permit?.vehicle?.year || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                  lg: 1.5,
                }}
              >
                <AuturaDropdown
                  name='permit.vehicle.color'
                  label={t('permit.vehicle.color', { ns: 'entities' })}
                  value={permit?.vehicle?.color || ''}
                  setValue={handleTextFieldChange}
                  options={state.colors}
                  readonly={!canEdit}
                  disabled={
                    !permit?.permitType?.data?.fieldConfig?.color?.enabled
                  }
                  required={
                    permit?.permitType?.data?.fieldConfig?.color?.required
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaMakeModelField
                  make={permit?.vehicle?.make || ''}
                  setMake={(make: string | null) => {
                    setPermit({
                      ...permit,
                      vehicle: { ...permit.vehicle, make: make || '' },
                    });
                  }}
                  model={permit?.vehicle?.model || ''}
                  setModel={(model: string | null) => {
                    setPermit({
                      ...permit,
                      vehicle: { ...permit.vehicle, model: model || '' },
                    });
                  }}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.vehicle.vin', { ns: 'entities' })}
                  name='permit.vehicle.vin'
                  value={permit?.vehicle?.vin || ''}
                  onChange={handleTextFieldChange}
                  readonly={!canEdit}
                  disabled={
                    !permit?.permitType?.data?.fieldConfig?.vin?.enabled
                  }
                  required={
                    permit?.permitType?.data?.fieldConfig?.vin?.required
                  }
                />
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default memo(PermitDetails);
